import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ImageUploadField from "../../components/ImageUploadField";
import { IMAGE_UPLOAD_HINT } from "../../utils/imageUploadPolicy";
import SidebarLayout from "./SidebarLayout";
import "./shopStyles.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const BusinessProfile = () => {
  const navigate = useNavigate();
  const currentRole =
    localStorage.getItem("ogestyle_role_override") ||
    localStorage.getItem("ogestyle_role");
  const isR1 = currentRole === "R1";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  const [businessId, setBusinessId] = useState(null);
  const [businessSlug, setBusinessSlug] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [businessData, setBusinessData] = useState({
    name: "",
    description: "",
    email: "",
    phone: "",
    website: "",
    socialMedia: {
      instagram: "",
      facebook: "",
      twitter: "",
    },
  });

  // Booth rental settings state
  const [boothRentalEnabled, setBoothRentalEnabled] = useState(false);

  // Business categories state
  const [businessCategories, setBusinessCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedNewCategory, setSelectedNewCategory] = useState("");
  const [updatingCategories, setUpdatingCategories] = useState(false);

  // Guard: Prevent R2 from accessing this page
  useEffect(() => {
    const role = localStorage.getItem("ogestyle_role");
    if (role === "R2") {
      // Redirect R2 to their personal admin profile
      navigate("/dashboard/admin/profile", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    fetchBusinessData();
  }, []);

  const fetchBusinessData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");

      let busId = null;

      // Option 1: Check staff profile (for staff members and owner-professionals)
      try {
        const staffRes = await axios.get(`${API_BASE_URL}/api/staff/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const staffData = staffRes.data.staff || staffRes.data;

        if (staffData) {
          busId =
            typeof staffData.businessId === "object"
              ? staffData.businessId?._id
              : staffData.businessId;
        }
      } catch (error) {
      }

      // Option 2: If no staff profile, get businessId from first shop
      if (!busId) {
        const shopsRes = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const shops = Array.isArray(shopsRes.data)
          ? shopsRes.data
          : shopsRes.data.shops || [];

        if (shops.length > 0) {
          busId = shops[0].businessId?._id || shops[0].businessId;
        }
      }

      if (!busId) {
        setAlert({
          show: true,
          message: "No business associated with your account",
          type: "error",
        });
        setLoading(false);
        return;
      }

      setBusinessId(busId);

      // Fetch business data from Business collection
      const businessRes = await axios.get(
        `${API_BASE_URL}/api/businesses/${busId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setBusinessData({
        name: businessRes.data.name || "",
        description: businessRes.data.description || "",
        email: businessRes.data.businessEmail || businessRes.data.email || "",
        phone: businessRes.data.businessPhone || businessRes.data.phone || "",
        website: businessRes.data.website || "",
        socialMedia: businessRes.data.socialMedia || {
          instagram: "",
          facebook: "",
          twitter: "",
        },
      });

      if (businessRes.data.slug) {
        setBusinessSlug(businessRes.data.slug);
      }

      // Set logo URL if exists
      if (businessRes.data.logo?.url) {
        setLogoUrl(businessRes.data.logo.url);
      }

      // Set business categories (type array)
      if (businessRes.data.type && Array.isArray(businessRes.data.type)) {
        setBusinessCategories(businessRes.data.type);
      }

      // Set booth rental settings
      if (
        businessRes.data.boothRentalSettings?.allowBoothRentals !== undefined
      ) {
        setBoothRentalEnabled(
          businessRes.data.boothRentalSettings.allowBoothRentals,
        );
      }

      // Fetch available business categories
      await fetchAvailableCategories();

      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: "Failed to load business data",
        type: "error",
      });
      setLoading(false);
    }
  };

  const fetchAvailableCategories = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.get(
        `${API_BASE_URL}/api/platform-settings/business-categories`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // API returns { types: [...], count: N }
      setAvailableCategories(response.data.types || []);
    } catch (error) {
      setAvailableCategories([]);
    }
  };

  const handleAddCategory = async () => {
    if (!selectedNewCategory) {
      setAlert({
        show: true,
        message: "Please select a category to add",
        type: "error",
      });
      return;
    }

    if (businessCategories.includes(selectedNewCategory)) {
      setAlert({
        show: true,
        message: "This category is already added",
        type: "error",
      });
      return;
    }

    try {
      setUpdatingCategories(true);
      const token = localStorage.getItem("ogestyle_token");

      const updatedCategories = [...businessCategories, selectedNewCategory];

      await axios.put(
        `${API_BASE_URL}/api/businesses/${businessId}`,
        { type: updatedCategories },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setBusinessCategories(updatedCategories);
      setSelectedNewCategory("");
      setAlert({
        show: true,
        message: "Business category added successfully!",
        type: "success",
      });
      setTimeout(() => setAlert({ show: false, message: "", type: "" }), 3000);
    } catch (error) {
      setAlert({
        show: true,
        message: "Failed to add category",
        type: "error",
      });
    } finally {
      setUpdatingCategories(false);
    }
  };

  const handleRemoveCategory = async (categoryToRemove) => {
    if (businessCategories.length === 1) {
      setAlert({
        show: true,
        message:
          "Cannot remove the last category. Business must have at least one category.",
        type: "error",
      });
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to remove this business category? This will affect your available service categories.`,
      )
    ) {
      return;
    }

    try {
      setUpdatingCategories(true);
      const token = localStorage.getItem("ogestyle_token");

      const updatedCategories = businessCategories.filter(
        (cat) => cat !== categoryToRemove,
      );

      await axios.put(
        `${API_BASE_URL}/api/businesses/${businessId}`,
        { type: updatedCategories },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setBusinessCategories(updatedCategories);
      setAlert({
        show: true,
        message: "Business category removed successfully!",
        type: "success",
      });
      setTimeout(() => setAlert({ show: false, message: "", type: "" }), 3000);
    } catch (error) {
      setAlert({
        show: true,
        message: "Failed to remove category",
        type: "error",
      });
    } finally {
      setUpdatingCategories(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("ogestyle_token");

      // Map frontend fields to backend schema (businessEmail/businessPhone)
      const payload = {
        name: businessData.name,
        description: businessData.description,
        businessEmail: businessData.email,
        businessPhone: businessData.phone,
        email: businessData.email, // Also save to legacy field for compatibility
        phone: businessData.phone, // Also save to legacy field for compatibility
        website: businessData.website,
        socialMedia: businessData.socialMedia,
        boothRentalSettings: {
          allowBoothRentals: boothRentalEnabled,
        },
      };

      await axios.put(`${API_BASE_URL}/api/businesses/${businessId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAlert({
        show: true,
        message: "Business profile updated successfully!",
        type: "success",
      });
      setTimeout(() => setAlert({ show: false, message: "", type: "" }), 3000);
      // U1: keep the owner sidebar in sync (Booth Rentals menu visibility)
      window.dispatchEvent(
        new Event("ogestyle:refresh-booth-rentals-visibility"),
      );
    } catch (error) {
      setAlert({
        show: true,
        message: "Failed to update profile",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    // Clear locally first so the UI responds immediately, and restore it if the server refuses.
    setLogoUrl("");

    try {
      await axios.delete(`${API_BASE_URL}/api/business/${businessId}/logo`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}`,
        },
      });
      setAlert({
        show: true,
        message: "Business logo removed",
        type: "success",
      });
      setTimeout(() => setAlert({ show: false, message: "", type: "" }), 3000);
    } catch (error) {
      setAlert({
        show: true,
        message: error.response?.data?.message || "Failed to remove logo",
        type: "error",
      });

      // The server still has the old logo, so reload it rather than leaving the page showing a
      // state the server does not agree with.
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/businesses/${businessId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}`,
            },
          },
        );
        setLogoUrl(response.data.logo?.url || "");
      } catch (_reloadError) {
        // The alert above already explains the failure.
      }
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="settings-page" style={{ padding: "24px", textAlign: "center" }}>Loading...</div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div
        className="settings-page"
        style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}
      >
        {/* Back Button */}
        <button
          onClick={() => navigate("/dashboard/settings")}
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "8px 16px",
            fontSize: "14px",
            color: "#374151",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
            transition: "all 0.2s",
            fontWeight: "500",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f9fafb";
            e.currentTarget.style.borderColor = "#d1d5db";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "white";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }}
        >
          <span>←</span>
          <span>Back to Settings</span>
        </button>

        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: "700",
              color: "#1a1a1a",
              margin: "0 0 8px 0",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Business Profile
          </h1>
          <p
            style={{
              fontSize: "14px",
              color: "#666",
              margin: 0,
              fontFamily: "Inter, sans-serif",
            }}
          >
            Update your business information and social media links
          </p>
        </div>

        {/* Alert */}
        {alert.show && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "20px",
              background: alert.type === "success" ? "#d1fae5" : "#fee2e2",
              color: alert.type === "success" ? "#065f46" : "#991b1b",
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
            }}
          >
            {alert.message}
          </div>
        )}

        {/* Share Business Profile Link */}
        {businessSlug && (
          <div
            style={{
              background: "#f0f6ff",
              border: "1px solid #c3d9f5",
              borderRadius: "10px",
              padding: "16px 20px",
              marginBottom: "24px",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "12px",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <span style={{ fontSize: "20px" }}>🔗</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#001d4a", marginBottom: "4px" }}>
                Your Business Profile Link — Share with customers
              </div>
              <div style={{ fontSize: "13px", color: "#2d6da3", wordBreak: "break-all" }}>
                {`${window.location.origin}/business/${businessSlug}`}
              </div>
            </div>
            <button
              onClick={() => {
                navigator.clipboard
                  .writeText(`${window.location.origin}/business/${businessSlug}`)
                  .then(() => {
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2500);
                });
              }}
              style={{
                padding: "8px 18px",
                borderRadius: "8px",
                border: "1px solid #2d6da3",
                background: linkCopied ? "#2d6da3" : "#fff",
                color: linkCopied ? "#fff" : "#2d6da3",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.2s",
                flexShrink: 0,
              }}
            >
              {linkCopied ? "✓ Copied!" : "📋 Copy Link"}
            </button>
          </div>
        )}

        {/* Form */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#374151",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Business Name *
            </label>
            <input
              type="text"
              value={businessData.name}
              onChange={(e) =>
                setBusinessData({ ...businessData, name: e.target.value })
              }
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "2px solid #e5e7eb",
                fontSize: "15px",
                boxSizing: "border-box",
                fontFamily: "Inter, sans-serif",
              }}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#374151",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Description
            </label>
            <textarea
              value={businessData.description}
              onChange={(e) =>
                setBusinessData({
                  ...businessData,
                  description: e.target.value,
                })
              }
              rows={4}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "2px solid #e5e7eb",
                fontSize: "15px",
                boxSizing: "border-box",
                fontFamily: "Inter, sans-serif",
                resize: "vertical",
              }}
            />
          </div>

          {/* Business Categories Section */}
          <div
            style={{
              marginBottom: "20px",
              padding: "20px",
              background: "#f9fafb",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
            }}
          >
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginTop: "0",
                marginBottom: "8px",
                color: "#374151",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Business Categories
            </h3>
            <p
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "16px",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Your business types determine available service categories. Add
              multiple categories as your business expands.
            </p>

            {/* Current Categories */}
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#4b5563",
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Current Categories:
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {businessCategories.length > 0 ? (
                  businessCategories.map((category) => {
                    const categoryInfo = availableCategories.find(
                      (cat) => cat.value === category,
                    );
                    return (
                      <div
                        key={category}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px 12px",
                          background: "#3b82f6",
                          color: "white",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        <span>{categoryInfo?.label || category}</span>
                        {businessCategories.length > 1 && (
                          <button
                            onClick={() => handleRemoveCategory(category)}
                            disabled={updatingCategories}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "white",
                              cursor: updatingCategories
                                ? "not-allowed"
                                : "pointer",
                              padding: "0",
                              fontSize: "16px",
                              lineHeight: "1",
                            }}
                            title="Remove category"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#9ca3af",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    No categories assigned
                  </span>
                )}
              </div>
            </div>

            {/* Add New Category */}
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#4b5563",
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Add New Category:
              </label>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-start",
                }}
              >
                <select
                  value={selectedNewCategory}
                  onChange={(e) => setSelectedNewCategory(e.target.value)}
                  disabled={updatingCategories}
                  style={{
                    flex: "1",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "14px",
                    fontFamily: "Inter, sans-serif",
                    background: "white",
                    cursor: updatingCategories ? "not-allowed" : "pointer",
                  }}
                >
                  <option value="">Select a category...</option>
                  {availableCategories
                    .filter((cat) => !businessCategories.includes(cat.value))
                    .map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                </select>
                <button
                  onClick={handleAddCategory}
                  disabled={!selectedNewCategory || updatingCategories}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "6px",
                    border: "none",
                    background:
                      selectedNewCategory && !updatingCategories
                        ? "#10b981"
                        : "#d1d5db",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor:
                      selectedNewCategory && !updatingCategories
                        ? "pointer"
                        : "not-allowed",
                    fontFamily: "Inter, sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  {updatingCategories ? "Adding..." : "+ Add"}
                </button>
              </div>
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginTop: "8px",
                  marginBottom: "0",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Adding categories will expand your available service templates
                (e.g., Salon + Med Spa = Hair services + Aesthetic services)
              </p>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#374151",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Email
              </label>
              <input
                type="email"
                value={businessData.email}
                onChange={(e) =>
                  setBusinessData({ ...businessData, email: e.target.value })
                }
                disabled={isR1}
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: isR1 ? "2px solid #d1d5db" : "2px solid #e5e7eb",
                  fontSize: "15px",
                  boxSizing: "border-box",
                  fontFamily: "Inter, sans-serif",
                  background: isR1 ? "#f3f4f6" : "white",
                  color: isR1 ? "#6b7280" : "#111827",
                  cursor: isR1 ? "not-allowed" : "text",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#374151",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Phone
              </label>
              <input
                type="tel"
                value={businessData.phone}
                onChange={(e) =>
                  setBusinessData({ ...businessData, phone: e.target.value })
                }
                disabled={isR1}
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: isR1 ? "2px solid #d1d5db" : "2px solid #e5e7eb",
                  fontSize: "15px",
                  boxSizing: "border-box",
                  fontFamily: "Inter, sans-serif",
                  background: isR1 ? "#f3f4f6" : "white",
                  color: isR1 ? "#6b7280" : "#111827",
                  cursor: isR1 ? "not-allowed" : "text",
                }}
              />
            </div>
          </div>

          {isR1 && (
            <p
              style={{
                marginTop: "-10px",
                marginBottom: "20px",
                fontSize: "12px",
                color: "#6b7280",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Email and phone are managed at account level and are read-only
              here.
            </p>
          )}

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#374151",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Website
            </label>
            <input
              type="url"
              value={businessData.website}
              onChange={(e) =>
                setBusinessData({ ...businessData, website: e.target.value })
              }
              placeholder="https://..."
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "2px solid #e5e7eb",
                fontSize: "15px",
                boxSizing: "border-box",
                fontFamily: "Inter, sans-serif",
              }}
            />
          </div>

          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              margin: "24px 0 16px 0",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Social Media
          </h3>

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#374151",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Instagram
            </label>
            <input
              type="text"
              value={businessData.socialMedia.instagram}
              onChange={(e) =>
                setBusinessData({
                  ...businessData,
                  socialMedia: {
                    ...businessData.socialMedia,
                    instagram: e.target.value,
                  },
                })
              }
              placeholder="@yourbusiness"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "2px solid #e5e7eb",
                fontSize: "15px",
                boxSizing: "border-box",
                fontFamily: "Inter, sans-serif",
              }}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#374151",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Facebook
            </label>
            <input
              type="text"
              value={businessData.socialMedia.facebook}
              onChange={(e) =>
                setBusinessData({
                  ...businessData,
                  socialMedia: {
                    ...businessData.socialMedia,
                    facebook: e.target.value,
                  },
                })
              }
              placeholder="facebook.com/yourbusiness"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "2px solid #e5e7eb",
                fontSize: "15px",
                boxSizing: "border-box",
                fontFamily: "Inter, sans-serif",
              }}
            />
          </div>

          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: "#374151",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Twitter
            </label>
            <input
              type="text"
              value={businessData.socialMedia.twitter}
              onChange={(e) =>
                setBusinessData({
                  ...businessData,
                  socialMedia: {
                    ...businessData.socialMedia,
                    twitter: e.target.value,
                  },
                })
              }
              placeholder="@yourbusiness"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "2px solid #e5e7eb",
                fontSize: "15px",
                boxSizing: "border-box",
                fontFamily: "Inter, sans-serif",
              }}
            />
          </div>

          {/* Feature Settings Section */}
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              margin: "32px 0 16px 0",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Feature Settings
          </h3>

          <div
            style={{
              padding: "16px",
              background: "#f9fafb",
              borderRadius: "8px",
              marginBottom: "24px",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                cursor: "pointer",
                fontSize: "15px",
                fontFamily: "Inter, sans-serif",
              }}
            >
              <input
                type="checkbox"
                checked={boothRentalEnabled}
                onChange={(e) => setBoothRentalEnabled(e.target.checked)}
                style={{
                  width: "20px",
                  height: "20px",
                  cursor: "pointer",
                }}
              />
              <div>
                <div
                  style={{
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "4px",
                  }}
                >
                  🏪 Enable Booth Rental
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                  }}
                >
                  Allow independent professionals to rent booth space at your
                  locations
                </div>
              </div>
            </label>
          </div>

          <button
            onClick={handleSaveProfile}
            disabled={saving}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: "8px",
              border: "none",
              background: "#3b82f6",
              color: "white",
              fontSize: "15px",
              fontWeight: "600",
              cursor: saving ? "not-allowed" : "pointer",
              fontFamily: "Inter, sans-serif",
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {/* Business Logo Section */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            marginTop: "24px",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              margin: "0 0 8px 0",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Business Logo
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: "#666",
              marginBottom: "20px",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Upload your business logo. This will appear on all your shop
            locations.
          </p>

          {/* One shared field: input + validation + HEIC + upload + preview + remove */}
          <ImageUploadField
            variant="inline"
            buttonLabel={logoUrl ? "Change Logo" : "Upload Logo"}
            value={logoUrl || ""}
            onChange={(url) => setLogoUrl(url)}
            onRemove={handleRemoveLogo}
            endpoint={`${API_BASE_URL}/api/business/${businessId}/upload-logo`}
            fieldName="logo"
            hint={`Recommended: 512x512px. ${IMAGE_UPLOAD_HINT}`}
            onError={(message) =>
              setAlert({ show: true, message, type: "error" })
            }
          />
        </div>
      </div>
    </SidebarLayout>
  );
};

export default BusinessProfile;
